import { type JSX, useEffect, useRef } from 'react';

import { cn } from '@change/ds-utils';

import type { RootShareRecommendations } from 'src/app/pages/starterDashboardV2/shared/hooks';

import type { Props as MainProps } from '../../index';
import { GroupRecommendationLoadingSkeleton } from '../GroupRecommendationLoadingSkeleton';
import { SocialGroupCard } from '../SocialGroupCard';

import { ChipsContainer } from './components/ChipsContainer';
import { ScrollButtons } from './components/ScrollButtons';
import { useRecommendationsCarousel } from './hooks/useRecommendationsCarousel';
import { useScrollControl } from './hooks/useScrollControl';

export function RecommendationsCarousel({
	shareRecommendations,
	isReskin = false,
	viewContext,
	loading = false,
}: {
	shareRecommendations: RootShareRecommendations;
	isReskin?: boolean;
	viewContext: MainProps['viewContext'];
	loading?: boolean;
}): JSX.Element | null {
	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	const { activeFilter, showChips, filteredRecommendations, handleFilterClick, createScrollClickHandler } =
		useRecommendationsCarousel(shareRecommendations);
	const { canScrollLeft, canScrollRight, updateScrollState, scrollByOneCard } = useScrollControl(scrollContainerRef);
	const handleScrollClick = createScrollClickHandler(scrollByOneCard);

	useEffect(() => {
		updateScrollState();
	}, [shareRecommendations, updateScrollState, activeFilter]);

	const handleScroll = () => {
		updateScrollState();
	};

	const reskinStyles = isReskin ? 'px-0 md:px-0 pr-4 md:pr-12' : '';

	return (
		<>
			{loading ? (
				<GroupRecommendationLoadingSkeleton count={5} />
			) : (
				<>
					<div
						className={cn('flex w-full flex-row justify-between px-8 duration-300 animate-in fade-in', reskinStyles)}
						data-testid="carousel-controls"
						data-qa="carousel-controls"
					>
						<div className="flex-grow">
							<ChipsContainer activeFilter={activeFilter} onFilterClick={handleFilterClick} showChips={showChips} />
						</div>
						<ScrollButtons
							data-qa="scroll-buttons-container"
							canScrollLeft={canScrollLeft}
							canScrollRight={canScrollRight}
							onScrollLeft={() => handleScrollClick('left')}
							onScrollRight={() => handleScrollClick('right')}
						/>
					</div>
					<div
						ref={scrollContainerRef}
						className="w-100 flex snap-x gap-4 overflow-x-auto duration-300 animate-in slide-in-from-top-4"
						sx={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
						onScroll={handleScroll}
						data-testid="recommendations-container"
						data-qa="recommendations-container"
						tabIndex={-1}
					>
						{filteredRecommendations.map((rec, i) => {
							return (
								<SocialGroupCard
									isFirst={i === 0}
									isLast={i === filteredRecommendations.length - 1}
									key={`${rec.platform}-${rec.index}`}
									recommendation={rec}
									platform={rec.platform}
									isReskin={isReskin}
									viewContext={viewContext}
								/>
							);
						})}
					</div>
				</>
			)}
		</>
	);
}
